import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ArticleCard from "@/components/feature/ArticleCard";
import NotFound from "@/pages/NotFound";
import { useSeo } from "@/hooks/useSeo";
import { articles, categories, trendingList } from "@/mocks/news";

type SortKey = "latest" | "hot";

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);
  const [sort, setSort] = useState<SortKey>("latest");
  const [activeTag, setActiveTag] = useState<string>("全部");

  const list = useMemo(
    () => articles.filter((a) => a.categorySlug === slug),
    [slug]
  );

  const tags = useMemo(() => ["全部", ...Array.from(new Set(list.flatMap((a) => a.tags)))], [list]);

  const visible = useMemo(() => {
    const filtered = activeTag === "全部" ? list : list.filter((a) => a.tags.includes(activeTag));
    return [...filtered].sort((a, b) => (sort === "hot" ? b.heat - a.heat : b.id - a.id));
  }, [list, activeTag, sort]);

  useSeo({
    title: category
      ? `${category.name}_${category.name}最新爆料持续更新 - 沸点爆料`
      : "频道不存在 - 沸点爆料",
    description: category
      ? `${category.desc}沸点爆料${category.name}频道持续更新中，追踪刚刚发生的最新动态，汇总今日热点爆料不遗漏。`
      : "抱歉，你访问的频道不存在。",
    keywords: category ? `${category.name},热点爆料,热点爆料今日,${category.name}最新` : "热点爆料",
    path: `/category/${slug}`,
    ogType: "website",
    jsonLd: category
      ? [
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${category.name}频道`,
            description: category.desc,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页" },
              { "@type": "ListItem", position: 2, name: category.name },
            ],
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: `${category.name}最新爆料`,
            itemListElement: list.map((a, i) => ({
              "@type": "ListItem",
              position: i + 1,
              name: a.title,
            })),
          },
        ]
      : undefined,
  });

  if (!category) {
    return <NotFound />;
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      <main className="flex-1 w-full">
        <div className="w-full bg-background-100 border-b border-background-200">
          <div className="w-full px-4 md:px-6 py-8 md:py-12">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: category.name }]} />
            <div className="mt-4 flex items-start gap-4">
              <span
                className={`shrink-0 w-14 h-14 flex items-center justify-center rounded-lg text-3xl ${
                  category.accent === "primary"
                    ? "bg-primary-100 text-primary-700"
                    : category.accent === "accent"
                    ? "bg-accent-100 text-accent-800"
                    : "bg-secondary-100 text-secondary-800"
                }`}
              >
                <i className={category.icon}></i>
              </span>
              <div>
                <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 tracking-tight">
                  {category.name}
                </h1>
                <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">{category.desc}</p>
                <p className="mt-2 text-xs text-foreground-500">当前频道共收录 {list.length} 条爆料内容</p>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
            <div className="lg:col-span-8">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
                <div className="flex items-center gap-1 p-1 rounded-full bg-background-100 border border-background-200 w-fit">
                  <button
                    type="button"
                    onClick={() => setSort("latest")}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      sort === "latest" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    最新更新
                  </button>
                  <button
                    type="button"
                    onClick={() => setSort("hot")}
                    className={`px-4 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                      sort === "hot" ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    热度最高
                  </button>
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  {tags.map((tag) => (
                    <button
                      key={tag}
                      type="button"
                      onClick={() => setActiveTag(tag)}
                      className={`h-7 px-3 rounded-full text-xs border transition-colors cursor-pointer whitespace-nowrap ${
                        activeTag === tag
                          ? "bg-secondary-900 text-background-50 border-secondary-900"
                          : "bg-background-50 text-foreground-600 border-background-300 hover:border-secondary-400"
                      }`}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </div>

              {visible.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-background-300 rounded-lg">
                  <span className="w-12 h-12 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-2xl">
                    <i className="ri-inbox-line"></i>
                  </span>
                  <p className="mt-4 text-sm text-foreground-600">这个筛选下暂时还没有内容，换个标签试试。</p>
                  <button
                    type="button"
                    onClick={() => setActiveTag("全部")}
                    className="mt-4 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    查看全部内容
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  {visible.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              )}
            </div>

            <aside className="lg:col-span-4 flex flex-col gap-6">
              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <h2 className="font-heading font-bold text-base text-foreground-950 px-4 md:px-5 py-4 border-b border-background-200 bg-background-100">
                  全部频道
                </h2>
                <ul className="p-2">
                  {categories.map((cat) => (
                    <li key={cat.slug}>
                      <Link
                        to={`/category/${cat.slug}`}
                        className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${
                          cat.slug === slug
                            ? "bg-primary-100 text-primary-800"
                            : "text-foreground-700 hover:bg-background-100"
                        }`}
                      >
                        <i className={cat.icon}></i>
                        {cat.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-background-50 border border-background-200 rounded-lg overflow-hidden">
                <h2 className="font-heading font-bold text-base text-foreground-950 px-4 md:px-5 py-4 border-b border-background-200 bg-background-100">
                  今日热榜 TOP 5
                </h2>
                <ol className="divide-y divide-background-200">
                  {trendingList.slice(0, 5).map((item) => (
                    <li key={item.rank}>
                      <Link to={`/article/${item.id}`} className="flex items-start gap-3 px-4 md:px-5 py-3 hover:bg-background-100 transition-colors">
                        <span className="shrink-0 w-5 h-5 flex items-center justify-center rounded text-xs font-bold bg-primary-100 text-primary-700">
                          {item.rank}
                        </span>
                        <span className="text-sm text-foreground-800 leading-snug line-clamp-2">{item.title}</span>
                      </Link>
                    </li>
                  ))}
                </ol>
              </div>

              <div className="rounded-lg bg-accent-100 border border-accent-200 p-5">
                <h2 className="font-heading font-bold text-base text-accent-900">手里有线索？</h2>
                <p className="mt-2 text-sm text-accent-900/80 leading-relaxed">
                  把你看到的、听到的告诉我们，我们会认真核实后再发布。
                </p>
                <Link
                  to="/contact"
                  className="mt-4 inline-flex items-center gap-2 h-9 px-4 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-megaphone-line"></i>
                  </span>
                  我要爆料
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </main>

      <Footer />
      <BackToTop />
    </div>
  );
}